import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import useCylinderSummary from '../hooks/useCylinderSummary';
import { APPROVALS_CHANGED, APPROVAL_APPLIED } from './ApprovalsPanel';
import { formatDateTime, formatDay } from '../utils/formatDate';
import { GASES, GAS_LABEL, gasLabel, isCgcClient, CGC_CLIENT } from '../utils/gases';

// Normalizes a client name for duplicate comparison — case, extra whitespace,
// and common punctuation shouldn't be enough to let the same client in twice.
const normalizeClientName = (str) =>
  str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:'"]/g, '')
    .replace(/\s*-\s*/g, '-');

const levenshteinDistance = (a, b) => {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp[m][n];
};

const nameSimilarity = (a, b) => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(a, b) / maxLen;
};

const SIMILARITY_THRESHOLD = 0.82;

const SalesDashboard = ({ addClient, editClient, deleteClient, mergeClients }) => {
  const [cylindersToAdd, setCylindersToAdd] = useState(0);
  // The gas of the empties received, and for CGC's own empties which client brought them back (backend utils/cgcTanks.js).
  const [contributionGas, setContributionGas] = useState('');
  const [contributionBroughtBy, setContributionBroughtBy] = useState('');
  const [clientName, setClientName] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [orphanedClientNames, setOrphanedClientNames] = useState([]);
  const [clientDetails, setClientDetails] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [pelliques, setPelliques] = useState([]);
  const [cylindersToDispatch, setCylindersToDispatch] = useState(0);
  const [dispatchNote, setDispatchNote] = useState('');
  // Entrega (we deliver — confirmed later with the ✓ button in the despachos table) or retiro (the client took
  // them — delivered now). Pre-selected from the client's choice in CG Connect when the note has a Ventas pedido.
  const [dispatchMethod, setDispatchMethod] = useState('entrega');
  const [dispatchPreview, setDispatchPreview] = useState(null);
  const [deliveringDispatch, setDeliveringDispatch] = useState(null); // { entry, received }
  const [deliverError, setDeliverError] = useState('');
  // Devoluciones the client or Ventas asked for, waiting for the tanks (GET /api/devoluciones).
  const [pendingDevoluciones, setPendingDevoluciones] = useState([]);
  const [receivingDev, setReceivingDev] = useState(null); // { request, quantity }
  const [receiveDevError, setReceiveDevError] = useState('');
  // Ventas facturas AND conduces matching the note number typed (or, before
  // that's typed, the client already picked for this despacho) — see
  // NewDispatchForm's twin of this in manifold-pwa/src/views/sales/DispatchTab.jsx
  // and the shared CLAUDE.md "Ventas integration" section.
  const [dispatchDocumentSuggestions, setDispatchDocumentSuggestions] = useState([]);
  // Picking a suggestion sets dispatchNote, which would otherwise immediately
  // re-trigger the search effect below and pop the dropdown back open with
  // that same document as its only match — skip exactly one search after a pick.
  const skipNextDispatchDocumentSearch = useRef(false);
  const [dispatchDestinationClient, setDispatchDestinationClient] = useState('');
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingContribution, setPendingContribution] = useState(null);
  const [selectedContributions, setSelectedContributions] = useState([]);
  const [dispatchError, setDispatchError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDispatchSuccessModal, setShowDispatchSuccessModal] = useState(false);
  const [dispatchSuccessMessage, setDispatchSuccessMessage] = useState('');

  const [showManageModal, setShowManageModal] = useState(false);
  const [manageGroup, setManageGroup] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: '', type: 'CC', date: '', gas: '', broughtBy: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [showDispatchEditModal, setShowDispatchEditModal] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [dispatchEditForm, setDispatchEditForm] = useState({
    noteNumber: '',
    dispatchedBy: '',
    destinationClient: '',
    contributions: [],
  });
  const [dispatchEditSaving, setDispatchEditSaving] = useState(false);
  const [dispatchEditError, setDispatchEditError] = useState('');

  const [showDispatchDeleteModal, setShowDispatchDeleteModal] = useState(false);
  const [dispatchDeleteTarget, setDispatchDeleteTarget] = useState(null);
  const [dispatchDeleteSaving, setDispatchDeleteSaving] = useState(false);
  const [dispatchDeleteError, setDispatchDeleteError] = useState('');

  const [showDispatchReturnModal, setShowDispatchReturnModal] = useState(false);
  const [returningDispatch, setReturningDispatch] = useState(null);
  const [dispatchReturnLines, setDispatchReturnLines] = useState([]);
  const [dispatchReturnReason, setDispatchReturnReason] = useState('');
  const [dispatchReturnSaving, setDispatchReturnSaving] = useState(false);
  const [dispatchReturnError, setDispatchReturnError] = useState('');

  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClientNameInput, setNewClientNameInput] = useState('');
  const [addClientError, setAddClientError] = useState('');
  const [addClientSaving, setAddClientSaving] = useState(false);
  const [addClientSimilarMatches, setAddClientSimilarMatches] = useState([]);

  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editClientOldName, setEditClientOldName] = useState('');
  const [editClientNewName, setEditClientNewName] = useState('');
  const [editClientError, setEditClientError] = useState('');
  const [editClientSaving, setEditClientSaving] = useState(false);
  const [editClientSimilarMatches, setEditClientSimilarMatches] = useState([]);

  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [deleteClientTarget, setDeleteClientTarget] = useState('');
  const [deleteClientAdminUser, setDeleteClientAdminUser] = useState('');
  const [deleteClientAdminPass, setDeleteClientAdminPass] = useState('');
  const [deleteClientError, setDeleteClientError] = useState('');
  const [deleteClientSaving, setDeleteClientSaving] = useState(false);
  const [showDeleteClientSuccessModal, setShowDeleteClientSuccessModal] = useState(false);
  const [deleteClientSuccessMessage, setDeleteClientSuccessMessage] = useState('');

  const [mergeSelectMode, setMergeSelectMode] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeSelectedSources, setMergeSelectedSources] = useState([]);
  const [showMergeClientModal, setShowMergeClientModal] = useState(false);
  const [mergeClientError, setMergeClientError] = useState('');
  const [mergeClientSaving, setMergeClientSaving] = useState(false);
  const [showMergeClientSuccessModal, setShowMergeClientSuccessModal] = useState(false);
  const [mergeClientSuccessMessage, setMergeClientSuccessMessage] = useState('');

  const [contributionPage, setContributionPage] = useState(1);
  const CONTRIBUTIONS_PER_PAGE = 10;
  const [contributionSearch, setContributionSearch] = useState('');

  const [dispatchFilter, setDispatchFilter] = useState('today');
  const [customDispatchMonth, setCustomDispatchMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dispatchPage, setDispatchPage] = useState(1);
  const DISPATCHES_PER_PAGE = 10;
  const [dispatchSearch, setDispatchSearch] = useState('');

  const {
    received,
    remaining,
    contributions,
    refreshSummary,
    refreshContributions,
  } = useCylinderSummary();

  // Adjusting "En Planta" / "Por Llenar" from Sales mirrors the same
  // admin-adjustment endpoints the admin dashboard uses — see
  // handleConfirmAdjust below for the fresh-refetch-then-delta pattern.
  const [adjustField, setAdjustField] = useState(null); // 'received' | 'remaining' | null
  const [adjustValue, setAdjustValue] = useState(0);
  const [adjustConfirmOpen, setAdjustConfirmOpen] = useState(false);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  const ADJUST_FIELD_LABEL = { received: 'En Planta', remaining: 'Por Llenar' };
  const adjustCurrentValue = adjustField === 'received' ? received : remaining;

  const openAdjustEdit = (field) => {
    setAdjustField(field);
    setAdjustValue(field === 'received' ? received : remaining);
    setAdjustError('');
  };

  // Every adjustment says why (the server refuses one without a reason).
  const [adjustReason, setAdjustReason] = useState('');
  const handleReviewAdjust = () => {
    if (!Number.isFinite(adjustValue) || adjustValue < 0) {
      setAdjustError('Ingrese un número válido, no negativo.');
      return;
    }
    if (adjustValue === adjustCurrentValue) {
      setAdjustError('No se realizó ningún cambio.');
      return;
    }
    setAdjustError('');
    setAdjustReason('');
    setAdjustConfirmOpen(true);
  };

  const handleConfirmAdjust = async () => {
    setAdjustSaving(true);
    setAdjustError('');
    try {
      const freshRes = await fetch('/api/cylinders/summary');
      const fresh = await freshRes.json();

      const endpoint = adjustField === 'received' ? '/api/cylinders/admin-adjustment' : '/api/fillings/admin-adjustment';
      const delta = adjustField === 'received'
        ? adjustValue - (fresh.received ?? received)
        : (fresh.remaining ?? remaining) - adjustValue;

      if (delta !== 0) {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ delta, reason: adjustReason.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'No se pudo guardar el ajuste.');
      }

      setAdjustField(null);
      refreshSummary();
    } catch (err) {
      setAdjustError(err.message || 'No se pudo guardar el ajuste.');
    } finally {
      setAdjustSaving(false);
      setAdjustConfirmOpen(false);
    }
  };

  const getStartOfDay = (date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getStartOfWeek = (date) => {
    const start = getStartOfDay(date);
    const day = start.getDay();
    const diff = day === 0 ? 6 : day - 1;
    start.setDate(start.getDate() - diff);
    return start;
  };

  const getStartOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getEntryRemaining = (entry) => {
    const amount = entry.amount || 0;
    const dispatchedSoFar = entry.dispatchedSoFar || 0;

    return entry.remaining ?? amount - dispatchedSoFar;
  };

  const groupedContributions = useMemo(() => {
    const grouped = {};

    [...contributions]
      .filter((entry) => {
        if (entry.type === 'Ajuste Administrativo') return false; // not a real client

        const amount = entry.amount || 0;
        const remainingAmount = getEntryRemaining(entry);

        return amount > 0 && remainingAmount > 0 && entry.dispatched !== true;
      })
      .forEach((entry) => {
        const client = entry.client || 'Sin Cliente';
        const amount = entry.amount || 0;
        const remainingAmount = getEntryRemaining(entry);
        const entryDate = entry.date ? new Date(entry.date) : null;
        // One row per client and gas, so a despacho never mixes gases (contributions from before gases were
        // recorded have none and make their own row).
        const key = `${client}|${entry.gas || ''}`;

        if (!grouped[key]) {
          grouped[key] = {
            key,
            client,
            gas: entry.gas || '',
            totalOriginal: 0,
            totalRemaining: 0,
            latestDate: entryDate,
            entries: [],
          };
        }

        grouped[key].totalOriginal += amount;
        grouped[key].totalRemaining += remainingAmount;

        if (
          entryDate &&
          (!grouped[key].latestDate || entryDate > grouped[key].latestDate)
        ) {
          grouped[key].latestDate = entryDate;
        }

        grouped[key].entries.push({
          ...entry,
          remainingForDispatch: remainingAmount,
        });
      });

    return Object.values(grouped)
      .map((group) => ({
        ...group,
        entries: group.entries.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        ),
      }))
      .sort((a, b) => new Date(b.latestDate) - new Date(a.latestDate));
  }, [contributions]);

  // Drives a small "adjustment happened today" pill on the contributions
  // table — the table itself no longer shows admin adjustments as if they
  // were a client, so this is the only remaining signal that one occurred.
  const todaysAdjustments = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return contributions.filter(
      (entry) => entry.type === 'Ajuste Administrativo' && entry.date && new Date(entry.date) >= startOfDay
    );
  }, [contributions]);
  const hasAdjustmentToday = todaysAdjustments.length > 0;
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);

  useEffect(() => {
    if (!showManageModal || !manageGroup) return;

    const updated = groupedContributions.find(
      (g) => g.client === manageGroup.client
    );

    if (!updated) {
      setShowManageModal(false);
      setManageGroup(null);
    } else if (updated !== manageGroup) {
      setManageGroup(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedContributions, showManageModal]);

  const getActingUserName = () =>
    localStorage.getItem('userName') ||
    localStorage.getItem('username') ||
    localStorage.getItem('name') ||
    'Usuario';

  const openManageModal = (group) => {
    setManageGroup(group);
    setShowManageModal(true);
  };

  const formatDateInput = (date) => {
    const d = date ? new Date(date) : new Date();
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  // Dominican Republic convention: DD/MM/YYYY. Uses local time components
  // (unlike utils/formatDate's formatDateOnly, entry.date here is a real
  // timestamp, not a UTC-midnight date-only field).
  const formatDateShort = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  };

  const openEditModal = (entry) => {
    setApprovalReason('');
    setEditingEntry(entry);
    setEditForm({
      quantity: entry.amount,
      type: entry.type || 'CC',
      date: formatDateInput(entry.date),
      gas: entry.gas || '',
      broughtBy: entry.broughtBy || '',
    });
    setEditError('');
    setShowEditModal(true);
  };

  // Sales need an admin's authorization to edit or delete a contribution or a despacho: the server answers 202
  // (nothing changed yet — an admin's approval carries it out). Admins never get a 202.
  const [approvalNotice, setApprovalNotice] = useState('');
  // The reason a sales user must give with the request (one dialog is open at a time; cleared when one opens).
  const [approvalReason, setApprovalReason] = useState('');
  const needsApproval = localStorage.getItem('userType') !== 'admin';
  const approvalFields = needsApproval ? { approvalReason: approvalReason.trim() } : {};
  const approvalReasonField = needsApproval && (
    <div className="form-group">
      <label className="form-label">Razón del cambio (obligatoria)</label>
      <textarea
        className="form-control"
        rows={2}
        maxLength={300}
        placeholder="Un administrador debe autorizarlo: explica por qué hace falta."
        value={approvalReason}
        onChange={(e) => setApprovalReason(e.target.value)}
      />
    </div>
  );
  const askedForApproval = (res, data) => {
    if (res.status !== 202 || !data.approvalRequested) return false;
    setApprovalNotice(data.message);
    window.dispatchEvent(new Event(APPROVALS_CHANGED)); // the header's badge
    return true;
  };
  const refreshAfterApproval = async () => {
    await refreshSummary();
    await refreshContributions();
    await fetchDispatches();
  };

  // An admin approved something from the header's Autorizaciones: the numbers here changed.
  useEffect(() => {
    const onApplied = () => refreshAfterApproval();
    window.addEventListener(APPROVAL_APPLIED, onApplied);
    return () => window.removeEventListener(APPROVAL_APPLIED, onApplied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveEdit = async () => {
    if (!editingEntry) return;

    const quantity = parseInt(editForm.quantity, 10);
    if (!quantity || quantity <= 0) {
      setEditError('Ingrese una cantidad válida.');
      return;
    }

    setEditSaving(true);
    setEditError('');

    try {
      const res = await fetch(`/api/cylinders/${editingEntry._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity,
          type: editForm.type,
          date: editForm.date,
          ...(editForm.gas ? { gas: editForm.gas } : {}),
          ...(editForm.broughtBy ? { broughtBy: editForm.broughtBy } : {}),
          userName: getActingUserName(),
          ...approvalFields,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al editar la contribución.');

      if (!askedForApproval(res, data)) {
        await refreshSummary();
        await refreshContributions();
      }

      setShowEditModal(false);
      setEditingEntry(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const requestDeleteEntry = (entry) => {
    setApprovalReason('');
    setDeleteTarget(entry);
    setDeleteError('');
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteEntry = async () => {
    if (!deleteTarget) return;

    setDeleteSaving(true);
    setDeleteError('');

    try {
      const res = await fetch(`/api/cylinders/${deleteTarget._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: getActingUserName(), ...approvalFields }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al eliminar la contribución.');

      if (!askedForApproval(res, data)) {
        await refreshSummary();
        await refreshContributions();
      }

      setShowDeleteConfirmModal(false);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleteSaving(false);
    }
  };

  const openDispatchEditModal = (dispatch) => {
    setApprovalReason('');
    setEditingDispatch(dispatch);
    setDispatchEditForm({
      noteNumber: dispatch.noteNumber === '—' ? '' : dispatch.noteNumber,
      dispatchedBy: dispatch.dispatchedBy,
      destinationClient: dispatch.contributions?.[0]?.destinationClient || '',
      contributions: dispatch.contributions.map((c) => ({
        entryId: c.entryId,
        client: c.client,
        amount: c.amount,
      })),
    });
    setDispatchEditError('');
    setShowDispatchEditModal(true);
  };

  const updateDispatchEditAmount = (index, value) => {
    setDispatchEditForm((f) => {
      const contributions = [...f.contributions];
      contributions[index] = { ...contributions[index], amount: value };
      return { ...f, contributions };
    });
  };

  const saveDispatchEdit = async () => {
    if (!editingDispatch) return;

    if (!dispatchEditForm.noteNumber.trim()) {
      setDispatchEditError('Ingrese un número de documento válido.');
      return;
    }

    const contributions = dispatchEditForm.contributions.map((c) => ({
      entryId: c.entryId,
      client: c.client,
      amount: parseInt(c.amount, 10),
      destinationClient: dispatchEditForm.destinationClient || undefined,
    }));

    if (contributions.some((c) => !c.amount || c.amount <= 0)) {
      setDispatchEditError('Todas las cantidades deben ser mayores a cero.');
      return;
    }

    setDispatchEditSaving(true);
    setDispatchEditError('');

    try {
      const res = await fetch(`/api/dispatches/${editingDispatch._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          noteNumber: dispatchEditForm.noteNumber.trim(),
          dispatchedBy: dispatchEditForm.dispatchedBy || getActingUserName(),
          contributions,
          userName: getActingUserName(),
          ...approvalFields,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al editar el despacho.')
        );
      }

      if (!askedForApproval(res, data)) await refreshAfterApproval();

      setShowDispatchEditModal(false);
      setEditingDispatch(null);
    } catch (err) {
      setDispatchEditError(err.message);
    } finally {
      setDispatchEditSaving(false);
    }
  };

  const requestDeleteDispatch = (dispatch) => {
    setApprovalReason('');
    setDispatchDeleteTarget(dispatch);
    setDispatchDeleteError('');
    setShowDispatchDeleteModal(true);
  };

  const confirmDeleteDispatch = async () => {
    if (!dispatchDeleteTarget) return;

    setDispatchDeleteSaving(true);
    setDispatchDeleteError('');

    try {
      const res = await fetch(`/api/dispatches/${dispatchDeleteTarget._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: getActingUserName(), ...approvalFields }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al eliminar el despacho.')
        );
      }

      if (!askedForApproval(res, data)) await refreshAfterApproval();

      setShowDispatchDeleteModal(false);
      setDispatchDeleteTarget(null);
    } catch (err) {
      setDispatchDeleteError(err.message);
    } finally {
      setDispatchDeleteSaving(false);
    }
  };

  // Devolución: some (or all) tanks from a despacho weren't accepted by the
  // client and are coming back — already filled — to the plant. Builds one
  // editable line per contribution, netting out whatever's already been
  // returned on this same dispatch so the max input reflects what's really
  // still outstanding.
  const openDispatchReturnModal = (dispatch) => {
    const alreadyReturnedByEntry = {};
    (dispatch.returns || []).forEach((r) => {
      alreadyReturnedByEntry[r.entryId] = (alreadyReturnedByEntry[r.entryId] || 0) + (r.quantity || 0);
    });

    const lines = (dispatch.contributions || []).map((c) => {
      const alreadyReturned = alreadyReturnedByEntry[c.entryId] || 0;
      return {
        entryId: c.entryId,
        client: c.client,
        originalAmount: c.amount,
        alreadyReturned,
        remaining: c.amount - alreadyReturned,
        returnQty: '',
      };
    });

    setReturningDispatch(dispatch);
    setDispatchReturnLines(lines);
    setDispatchReturnReason('');
    setDispatchReturnError('');
    setShowDispatchReturnModal(true);
  };

  const updateDispatchReturnQty = (index, value) => {
    setDispatchReturnLines((lines) => {
      const next = [...lines];
      next[index] = { ...next[index], returnQty: value };
      return next;
    });
  };

  const saveDispatchReturn = async () => {
    if (!returningDispatch) return;

    const returns = dispatchReturnLines
      .map((l) => ({ entryId: l.entryId, quantity: parseInt(l.returnQty, 10), remaining: l.remaining }))
      .filter((l) => Number.isFinite(l.quantity) && l.quantity > 0);

    if (returns.length === 0) {
      setDispatchReturnError('Ingrese al menos una cantidad a devolver.');
      return;
    }

    const overLimit = returns.find((l) => l.quantity > l.remaining);
    if (overLimit) {
      setDispatchReturnError('Una de las cantidades excede lo pendiente para esa línea.');
      return;
    }

    setDispatchReturnSaving(true);
    setDispatchReturnError('');

    try {
      const res = await fetch(`/api/dispatches/${returningDispatch._id}/return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returns: returns.map(({ entryId, quantity }) => ({ entryId, quantity })),
          reason: dispatchReturnReason.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.details ? `${data.error} ${data.details.join(' ')}` : (data.error || 'Error al registrar la devolución.')
        );
      }

      await refreshSummary();
      await refreshContributions();
      await fetchDispatches();

      setShowDispatchReturnModal(false);
      setReturningDispatch(null);

      const totalReturned = returns.reduce((s, l) => s + l.quantity, 0);
      setDispatchSuccessMessage(`Devolución registrada: ${totalReturned} cilindros del despacho "${returningDispatch.noteNumber}".`);
      setShowDispatchSuccessModal(true);
    } catch (err) {
      setDispatchReturnError(err.message);
    } finally {
      setDispatchReturnSaving(false);
    }
  };

  const dispatchedToday = useMemo(() => {
    const start = getStartOfDay(new Date());
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    return dispatches.reduce((sum, entry) => {
      if (!entry.rawTimestamp) return sum;
      const d = new Date(entry.rawTimestamp);
      return d >= start && d < end ? sum + (entry.cantidad || 0) : sum;
    }, 0);
  }, [dispatches]);

  const filteredDispatches = useMemo(() => {
    const now = new Date();

    return dispatches.filter((entry) => {
      if (!entry.rawTimestamp) return false;

      const dispatchDate = new Date(entry.rawTimestamp);

      if (dispatchFilter === 'today') {
        const start = getStartOfDay(now);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        return dispatchDate >= start && dispatchDate < end;
      }

      if (dispatchFilter === 'week') {
        const start = getStartOfWeek(now);
        return dispatchDate >= start && dispatchDate <= now;
      }

      if (dispatchFilter === 'month') {
        const start = getStartOfMonth(now);
        return dispatchDate >= start && dispatchDate <= now;
      }

      if (dispatchFilter === 'custom') {
        if (!customDispatchMonth) return true;

        const [year, month] = customDispatchMonth.split('-').map(Number);
        const start = new Date(year, month - 1, 1);
        const end = new Date(year, month, 1);

        return dispatchDate >= start && dispatchDate < end;
      }

      return true;
    });
  }, [dispatches, dispatchFilter, customDispatchMonth]);

  const searchedDispatches = useMemo(() => {
    const term = dispatchSearch.trim().toLowerCase();
    if (!term) return filteredDispatches;

    return filteredDispatches.filter((entry) =>
      (entry.noteNumber || '').toLowerCase().includes(term)
    );
  }, [filteredDispatches, dispatchSearch]);

  const totalDispatchPages = Math.max(
    1,
    Math.ceil(searchedDispatches.length / DISPATCHES_PER_PAGE)
  );

  const paginatedDispatches = searchedDispatches.slice(
    (dispatchPage - 1) * DISPATCHES_PER_PAGE,
    dispatchPage * DISPATCHES_PER_PAGE
  );

  const searchedContributions = useMemo(() => {
    const term = contributionSearch.trim().toLowerCase();
    if (!term) return groupedContributions;

    return groupedContributions.filter((group) =>
      (group.client || '').toLowerCase().includes(term)
    );
  }, [groupedContributions, contributionSearch]);

  const totalContributionPages = Math.max(
    1,
    Math.ceil(searchedContributions.length / CONTRIBUTIONS_PER_PAGE)
  );

  const paginatedContributions = searchedContributions.slice(
    (contributionPage - 1) * CONTRIBUTIONS_PER_PAGE,
    contributionPage * CONTRIBUTIONS_PER_PAGE
  );

  useEffect(() => {
    if (contributionPage > totalContributionPages) {
      setContributionPage(totalContributionPages);
    }
  }, [contributionPage, totalContributionPages]);

  useEffect(() => {
    setContributionPage(1);
  }, [contributionSearch]);

  useEffect(() => {
    setDispatchPage(1);
  }, [dispatchFilter, customDispatchMonth, dispatchSearch]);

  useEffect(() => {
    if (dispatchPage > totalDispatchPages) {
      setDispatchPage(totalDispatchPages);
    }
  }, [dispatchPage, totalDispatchPages]);

  const fetchClientsFromDB = async () => {
    try {
      const res = await fetch('/api/clients');
      const data = await res.json();

      if (Array.isArray(data)) {
        setClientsList(data);
      }
    } catch (err) {
      console.error('❌ Error fetching clients:', err);
    }
  };

  // Client names still referenced by historical contributions/dispatches/
  // orders/pelliques whose Client doc was already deleted (pre-dating the
  // merge feature, via plain "Eliminar cliente") — offered as extra,
  // pre-flagged merge sources so that old delete-and-forget mess can still
  // be consolidated even though the client itself no longer exists.
  const fetchOrphanedClients = async () => {
    try {
      const res = await fetch('/api/clients/orphaned');
      const data = await res.json();

      if (Array.isArray(data)) {
        setOrphanedClientNames(data);
      }
    } catch (err) {
      console.error('❌ Error fetching orphaned clients:', err);
    }
  };

  const fetchClientDetails = async () => {
    try {
      const res = await fetch('/api/clients/details');
      const data = await res.json();

      if (Array.isArray(data)) {
        setClientDetails(data);
      }
    } catch (err) {
      console.error('❌ Error fetching client details:', err);
    }
  };

  const fetchDispatches = async () => {
    try {
      const res = await fetch('/api/dispatches');
      const data = await res.json();

      if (Array.isArray(data)) {
        setDispatches(
          data
            .map((entry) => {
              const rawTimestamp = entry.timestamp || entry.createdAt || entry.date;

              return {
                _id: entry._id,
                cantidad: entry.cantidad || entry.quantity || entry.amount || 0,
                dispatchedBy:
                  entry.userName ||
                  entry.username ||
                  entry.dispatchedBy ||
                  entry.createdBy ||
                  'Usuario',
                noteNumber: entry.noteNumber || entry.dispatchNote || '—',
                rawTimestamp,
                timestamp: rawTimestamp
                  ? formatDateTime(rawTimestamp)
                  : '—',
                contributions: Array.isArray(entry.contributions)
                  ? entry.contributions
                  : [],
                returns: Array.isArray(entry.returns) ? entry.returns : [],
                totalReturned: entry.totalReturned || 0,
                method: entry.method || null,
                delivered: !!entry.delivered,
                receivedQuantity: entry.receivedQuantity ?? null,
                ventas: entry.ventas || null,
              };
            })
            .sort((a, b) => new Date(b.rawTimestamp) - new Date(a.rawTimestamp))
        );
      }
    } catch (err) {
      console.error('❌ Error fetching dispatches:', err);
    }
  };

  const fetchPelliques = async () => {
    try {
      const res = await fetch('/api/pelliques');
      const data = await res.json();
      setPelliques(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching pelliques:', err);
    }
  };

  // Outstanding (not-yet-returned) pellique quantity per distributor —
  // "distributor" is the same client name shown in the contributions table,
  // since a pellique is tanks borrowed *from* that client, not received.
  const pelliqueOutstandingByClient = useMemo(() => {
    const byClient = {};
    pelliques.forEach((p) => {
      const outstanding = (p.quantity || 0) - (p.returnedQuantity || 0);
      if (outstanding <= 0 || !p.distributor) return;
      byClient[p.distributor] = (byClient[p.distributor] || 0) + outstanding;
    });
    return byClient;
  }, [pelliques]);

  useEffect(() => {
    fetchClientsFromDB();
    fetchOrphanedClients();
    fetchClientDetails();
    fetchDispatches();
    fetchPelliques();
  }, []);

  useEffect(() => {
    const total = selectedContributions.reduce(
      (sum, item) => sum + (item.dispatchAmount || 0),
      0
    );

    setCylindersToDispatch(total);
  }, [selectedContributions]);

  useEffect(() => {
    if (skipNextDispatchDocumentSearch.current) {
      skipNextDispatchDocumentSearch.current = false;
      return;
    }
    const term = (dispatchNote.trim().length >= 2 ? dispatchNote : selectedContributions[0]?.client || '').trim();
    if (term.length < 2) {
      setDispatchDocumentSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      fetch(`/api/ventas/document-search?q=${encodeURIComponent(term)}`)
        .then((res) => (res.ok ? res.json() : { documents: [] }))
        .then((data) => setDispatchDocumentSuggestions(data.documents || []))
        .catch(() => setDispatchDocumentSuggestions([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [dispatchNote, selectedContributions]);

  const pickDispatchDocumentSuggestion = (doc) => {
    skipNextDispatchDocumentSearch.current = true;
    setDispatchNote(doc.number);
    setDispatchDocumentSuggestions([]);
  };

  const handleClientSearch = (value) => {
    setClientName(value);

    if (value) {
      const matches = clientsList.filter((name) =>
        name.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredClients(matches);
      setShowDropdown(true);
    } else if (!mergeSelectMode) {
      setShowDropdown(false);
    }
  };

  const selectClient = (client) => {
    setClientName(client);
    setShowDropdown(false);
  };

  const handleAddCylinders = () => {
    if (!clientName || cylindersToAdd <= 0) {
      alert('Debe ingresar un cliente y una cantidad válida.');
      return;
    }
    if (!contributionGas) {
      alert('Indica el gas de estos cilindros.');
      return;
    }
    if (isCgcClient(clientName) && !contributionBroughtBy) {
      alert('Indica qué cliente trajo estos cilindros de CGC.');
      return;
    }

    const clientTrimmed = clientName.trim();
    const quantity = parseInt(cylindersToAdd);
    // Same rule as manifold-pwa's ContributionTab and the server (utils/inventoryWrites.js typeForClient): Caribbean Gas /
    // IntMed's empties are CC, everyone else's PC. (This used to compare against 'caribbean gas company', which never
    // matched the real client name, so its contributions from here were saved as PC.)
    const type =
      clientTrimmed.toLowerCase().replace(/\s+/g, ' ') === 'caribbean gas company / intmed' ? 'CC' : 'PC';

    setPendingContribution({
      client: clientTrimmed,
      quantity,
      type,
      gas: contributionGas,
      broughtBy: isCgcClient(clientTrimmed) ? contributionBroughtBy : undefined,
    });

    setShowConfirmModal(true);
  };

  const confirmAddCylinders = async () => {
    try {
      const res = await fetch('/api/cylinders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: pendingContribution.client,
          quantity: pendingContribution.quantity,
          type: pendingContribution.type,
          gas: pendingContribution.gas,
          ...(pendingContribution.broughtBy ? { broughtBy: pendingContribution.broughtBy } : {}),
          date: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || 'No se pudo guardar la contribución.');
      }

      await refreshSummary();
      await refreshContributions();
      await fetchClientsFromDB();

      setClientName('');
      setCylindersToAdd(0);
      setContributionGas('');
      setContributionBroughtBy('');
      setPendingContribution(null);
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      alert(err.message); // e.g. the client isn't on the list, or this role can't record inventory
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleAddClient = async (newClient) => {
    try {
      await addClient(newClient);
      await fetchClientsFromDB();
      selectClient(newClient);
    } catch (err) {
      console.error('❌ Error adding new client:', err);
    }
  };

  const openAddClientModal = (proposedName) => {
    setNewClientNameInput(proposedName);
    setAddClientError('');
    setAddClientSimilarMatches([]);
    setShowDropdown(false);
    setShowAddClientModal(true);
  };

  // Existing client names whose normalized form is a close (but not exact)
  // match to the given name — flags likely typos/duplicates without blocking
  // legitimately distinct clients that just happen to sound alike.
  const findSimilarClientNames = (name, excludeName = null) => {
    const normalized = normalizeClientName(name);

    return clientNames
      .filter((existing) => existing.trim().toLowerCase() !== (excludeName || '').trim().toLowerCase())
      .map((existing) => ({
        name: existing,
        ratio: nameSimilarity(normalized, normalizeClientName(existing)),
      }))
      .filter(({ ratio }) => ratio >= SIMILARITY_THRESHOLD && ratio < 1)
      .sort((a, b) => b.ratio - a.ratio);
  };

  const saveNewClient = async (trimmed) => {
    setAddClientSaving(true);
    setAddClientError('');

    try {
      await handleAddClient(trimmed);
      setShowAddClientModal(false);
      setNewClientNameInput('');
      setAddClientSimilarMatches([]);
    } catch (err) {
      setAddClientError('Error al guardar el cliente.');
    } finally {
      setAddClientSaving(false);
    }
  };

  const confirmAddClient = async () => {
    const trimmed = newClientNameInput.trim();

    if (!trimmed) {
      setAddClientError('Ingrese un nombre de cliente.');
      return;
    }

    const normalized = normalizeClientName(trimmed);
    const alreadyExists = clientNames.some(
      (name) => normalizeClientName(name) === normalized
    );

    if (alreadyExists) {
      setAddClientError('Ya existe un cliente con ese nombre.');
      return;
    }

    const similar = findSimilarClientNames(trimmed);
    if (similar.length > 0) {
      setAddClientSimilarMatches(similar);
      return;
    }

    await saveNewClient(trimmed);
  };

  const proceedAddClientAnyway = async () => {
    await saveNewClient(newClientNameInput.trim());
  };

  const openEditClientModal = (name) => {
    setEditClientOldName(name);
    setEditClientNewName(name);
    setEditClientError('');
    setEditClientSimilarMatches([]);
    setShowDropdown(false);
    setShowEditClientModal(true);
  };

  const saveEditedClientName = async (trimmed) => {
    setEditClientSaving(true);
    setEditClientError('');

    try {
      await editClient(editClientOldName, trimmed);

      await fetchClientsFromDB();
      await refreshSummary();
      await refreshContributions();
      await fetchDispatches();

      if (clientName.trim() === editClientOldName.trim()) {
        setClientName(trimmed);
      }

      setShowEditClientModal(false);
      setEditClientSimilarMatches([]);
    } catch (err) {
      setEditClientError(err.message || 'Error al editar el cliente.');
    } finally {
      setEditClientSaving(false);
    }
  };

  const confirmEditClientName = async () => {
    const trimmed = editClientNewName.trim();

    if (!trimmed) {
      setEditClientError('Ingrese un nombre de cliente.');
      return;
    }

    if (normalizeClientName(trimmed) === normalizeClientName(editClientOldName) && trimmed === editClientOldName) {
      setEditClientError('El nuevo nombre debe ser diferente al actual.');
      return;
    }

    const normalized = normalizeClientName(trimmed);
    const collision = clientNames.some(
      (name) =>
        name.trim().toLowerCase() !== editClientOldName.trim().toLowerCase() &&
        normalizeClientName(name) === normalized
    );

    if (collision) {
      setEditClientError('Ya existe un cliente con ese nombre.');
      return;
    }

    const similar = findSimilarClientNames(trimmed, editClientOldName);
    if (similar.length > 0) {
      setEditClientSimilarMatches(similar);
      return;
    }

    await saveEditedClientName(trimmed);
  };

  const proceedEditClientAnyway = async () => {
    await saveEditedClientName(editClientNewName.trim());
  };

  const requestDeleteClient = (name) => {
    setDeleteClientTarget(name);
    setDeleteClientAdminUser('');
    setDeleteClientAdminPass('');
    setDeleteClientError('');
    setShowDropdown(false);
    setShowDeleteClientModal(true);
  };

  const confirmDeleteClient = async () => {
    if (!deleteClientAdminUser.trim() || !deleteClientAdminPass) {
      setDeleteClientError('Ingrese usuario y contraseña de administrador.');
      return;
    }

    setDeleteClientSaving(true);
    setDeleteClientError('');

    try {
      const deletedName = deleteClientTarget;

      await deleteClient(
        deletedName,
        deleteClientAdminUser.trim(),
        deleteClientAdminPass
      );

      await fetchClientsFromDB();
      await fetchOrphanedClients();
      await fetchClientDetails();

      if (clientName.trim() === deletedName.trim()) {
        setClientName('');
      }

      setShowDeleteClientModal(false);
      setDeleteClientTarget('');
      setDeleteClientAdminUser('');
      setDeleteClientAdminPass('');

      setDeleteClientSuccessMessage(
        `El cliente "${deletedName}" fue eliminado correctamente.`
      );
      setShowDeleteClientSuccessModal(true);
    } catch (err) {
      setDeleteClientError(err.message || 'Error al eliminar el cliente.');
    } finally {
      setDeleteClientSaving(false);
    }
  };

  const requestMergeClient = (name) => {
    setMergeTarget(name);
    setMergeSelectedSources([]);
    setMergeSelectMode(true);
    setMergeClientError('');
  };

  const cancelMergeSelectMode = () => {
    setMergeSelectMode(false);
    setMergeTarget('');
    setMergeSelectedSources([]);
  };

  const toggleMergeSource = (name) => {
    setMergeSelectedSources((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const openMergeConfirm = () => {
    if (mergeSelectedSources.length === 0) return;
    setMergeClientError('');
    setShowDropdown(false);
    setShowMergeClientModal(true);
  };

  const confirmMergeClient = async () => {
    setMergeClientSaving(true);
    setMergeClientError('');

    try {
      const mergedSources = mergeSelectedSources;
      const mergedTarget = mergeTarget;

      await mergeClients(mergedSources, mergedTarget);

      await fetchClientsFromDB();
      await fetchOrphanedClients();
      await fetchClientDetails();
      await refreshSummary();
      await refreshContributions();
      await fetchDispatches();

      if (mergedSources.some((n) => n.trim() === clientName.trim())) {
        setClientName(mergedTarget);
      }

      setShowMergeClientModal(false);
      cancelMergeSelectMode();

      setMergeClientSuccessMessage(
        mergedSources.length === 1
          ? `El cliente "${mergedSources[0]}" fue fusionado en "${mergedTarget}". Todo su historial ahora aparece bajo "${mergedTarget}".`
          : `Los clientes ${mergedSources
              .map((n) => `"${n}"`)
              .join(', ')} fueron fusionados en "${mergedTarget}". Todo su historial ahora aparece bajo "${mergedTarget}".`
      );
      setShowMergeClientSuccessModal(true);
    } catch (err) {
      setMergeClientError(err.message || 'Error al fusionar los clientes.');
    } finally {
      setMergeClientSaving(false);
    }
  };

  const isDeletedClient = (name) =>
    !!name && clientsList.length > 0 && !clientNames.includes(name);

  const DeletedClientBadge = ({ name }) =>
    isDeletedClient(name) ? (
      <span
        className="badge ml-1"
        style={{
          backgroundColor: 'var(--mm-panel-2)',
          color: 'var(--mm-red)',
          border: '1px solid rgba(248,113,113,0.4)',
          fontSize: '0.7rem',
          fontWeight: 500,
        }}
      >
        Cliente eliminado
      </span>
    ) : null;

  const buildDispatchEntries = () => {
    return selectedContributions.flatMap((group) => {
      let remainingToDispatch = group.dispatchAmount || 0;
      const dispatchEntries = [];

      for (const entry of group.entries) {
        if (remainingToDispatch <= 0) break;

        const entryRemaining = entry.remainingForDispatch || 0;
        const amountToDispatch = Math.min(entryRemaining, remainingToDispatch);

        if (amountToDispatch > 0) {
          dispatchEntries.push({
            entryId: entry._id,
            amount: amountToDispatch,
            client: group.client,
            destinationClient: dispatchDestinationClient || undefined,
          });

          remainingToDispatch -= amountToDispatch;
        }
      }

      return dispatchEntries;
    });
  };

  const handleDispatch = async () => {
    try {
      console.log('📦 Enviando despacho:', selectedContributions);

      const dispatcherName =
        localStorage.getItem('userName') ||
        localStorage.getItem('username') ||
        localStorage.getItem('name') ||
        'Usuario';

      const payload = {
        noteNumber: dispatchNote,
        dispatchedBy: dispatcherName,
        userName: dispatcherName,
        entries: buildDispatchEntries(),
        method: dispatchMethod,
      };

      console.log('📦 Dispatch payload:', payload);

      const res = await fetch('/api/dispatches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || 'Error despachando cilindros');
      }

      const result = await res.json();

      const ventasNote = result.ventas?.status === 'vinculado'
        ? ` Pedido ${result.ventas.pedidoNumber} actualizado en CG Connect.`
        : result.ventas?.status === 'pendiente' ? ' Ventas no respondió; se le enviará automáticamente.' : '';
      setDispatchSuccessMessage(
        `${result.message || 'Despacho registrado con éxito.'}${dispatchMethod === 'retiro' ? ' Retirado en planta.' : ''}${ventasNote}`
      );
      setShowDispatchSuccessModal(true);

      await refreshSummary();
      await fetchDispatches();
      await refreshContributions();

      setSelectedContributions([]);
      setDispatchNote('');
      setDispatchDocumentSuggestions([]);
      setDispatchDestinationClient('');
      setDispatchMethod('entrega');
      setCylindersToDispatch(0);
      setShowDispatchModal(false);
    } catch (err) {
      console.error('❌ Error en el despacho:', err);
      alert(err.message); // e.g. more than the client has in the plant (the server checks it too)
    }
  };

  // Look up the note's Ventas pedido whenever the confirmation opens, and pre-select how the client wanted it.
  useEffect(() => {
    if (!showConfirmation) return;
    setDispatchPreview(null);
    setDispatchMethod('entrega');
    const note = dispatchNote.trim();
    if (!note) return;
    fetch(`/api/ventas/note-preview?note=${encodeURIComponent(note)}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data?.pedido) return;
        setDispatchPreview(data.pedido);
        if (data.pedido.legs?.filled === 'client_picks') setDispatchMethod('retiro');
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmation]);

  const fetchPendingDevoluciones = async () => {
    try {
      const res = await fetch('/api/devoluciones');
      const data = await res.json();
      setPendingDevoluciones(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching devoluciones:', err);
    }
  };
  useEffect(() => { fetchPendingDevoluciones(); }, []);

  // "Llegaron": the tanks of a requested devolución are back — into "En Planta" against the pedido's despachos.
  const handleReceiveDevolucion = async () => {
    const { request, quantity } = receivingDev;
    setReceiveDevError('');
    try {
      const res = await fetch(`/api/devoluciones/${request._id}/recibir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: Number(quantity) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo registrar la devolución.');
      setReceivingDev(null);
      setDispatchSuccessMessage(`${quantity} cilindros del pedido ${request.pedidoNumber} de vuelta en planta.`);
      setShowDispatchSuccessModal(true);
      await Promise.all([fetchPendingDevoluciones(), fetchDispatches(), refreshSummary(), refreshContributions()]);
    } catch (err) {
      setReceiveDevError(err.message);
    }
  };

  // ✓ in the despachos table: the client received an "entrega" despacho — how many tanks they actually got.
  const handleConfirmDelivery = async () => {
    const { entry, received } = deliveringDispatch;
    setDeliverError('');
    try {
      const res = await fetch(`/api/dispatches/${entry._id}/deliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ received: Number(received) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo confirmar la entrega.');
      setDeliveringDispatch(null);
      setDispatchSuccessMessage(
        `Entrega de "${entry.noteNumber}" confirmada: ${received} recibidos.${data.shortBy > 0 ? ` Los ${data.shortBy} que faltaron quedaron de vuelta en "En Planta".` : ''}`
      );
      setShowDispatchSuccessModal(true);
      await Promise.all([fetchDispatches(), refreshSummary(), refreshContributions()]);
    } catch (err) {
      setDeliverError(err.message);
    }
  };

  // Guards against dispatching tanks that are actually out on pellique loan:
  // if the selected client has any outstanding pellique, interrupt the
  // normal review step with a choice instead of silently letting the
  // dispatch draw down stock that isn't really "in planta".
  const [pelliqueWarningOpen, setPelliqueWarningOpen] = useState(false);
  const [pelliqueActionSaving, setPelliqueActionSaving] = useState(false);
  const [pelliqueActionError, setPelliqueActionError] = useState('');

  const handleReviewDispatch = () => {
    const client = selectedContributions[0]?.client;
    if (isCgcClient(client) && !dispatchDestinationClient) {
      setDispatchError('Los cilindros de Caribbean Gas / IntMed necesitan el cliente que los recibe: elige el Cliente Destino.');
      setShowErrorModal(true);
      return;
    }
    const outstanding = client ? pelliqueOutstandingByClient[client] || 0 : 0;
    if (outstanding > 0) {
      setPelliqueActionError('');
      setPelliqueWarningOpen(true);
    } else {
      setShowConfirmation(true);
    }
  };

  // Caps the requested amount at what's actually free (remaining minus
  // whatever's still out on loan), then continues to the normal confirm step
  // so the reduced total is visible before submitting.
  const handleAdjustToAvailableBalance = () => {
    const client = selectedContributions[0]?.client;
    const outstanding = pelliqueOutstandingByClient[client] || 0;
    const totalRemaining = selectedContributions[0]?.totalRemaining || 0;
    const availableBalance = Math.max(0, totalRemaining - outstanding);

    setSelectedContributions((prev) =>
      prev.map((entry) => ({ ...entry, dispatchAmount: Math.min(entry.dispatchAmount, availableBalance) }))
    );
    setPelliqueWarningOpen(false);
    setShowConfirmation(true);
  };

  // Marks every outstanding pellique for this client as returned, then
  // dispatches the originally-requested amount in full — this button is
  // itself the confirmation, so it skips the separate review modal.
  const handleReturnPelliqueAndDispatch = async () => {
    const client = selectedContributions[0]?.client;
    setPelliqueActionSaving(true);
    setPelliqueActionError('');
    try {
      const outstandingPelliques = pelliques.filter((p) => p.distributor === client && !p.returned);
      for (const p of outstandingPelliques) {
        const res = await fetch(`/api/pelliques/${p._id}/return`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'No se pudo devolver el pellique.');
        }
      }
      await fetchPelliques();
      setPelliqueWarningOpen(false);
      await handleDispatch();
    } catch (err) {
      setPelliqueActionError(err.message || 'No se pudo devolver el pellique.');
    } finally {
      setPelliqueActionSaving(false);
    }
  };

  const clientNames = clientsList;

  const isNewClient = clientName && !clientNames.includes(clientName.trim());

  const selectForDispatch = (group) => {
    if (selectedContributions.length > 0) {
      const selectedClient = selectedContributions[0].client;

      if (group.client !== selectedClient) {
        setDispatchError(
          'Los despachos deben realizarse por cliente. No se permite mezclar contribuciones.'
        );
        setShowErrorModal(true);
        return;
      }
    }

    const alreadyAdded = selectedContributions.some(
      (entry) => entry.key === group.key
    );

    if (alreadyAdded) return;

    if (group.totalRemaining <= 0) {
      alert('Este cliente ya no tiene cilindros pendientes para despachar.');
      return;
    }

    const groupWithDispatchAmount = {
      ...group,
      _id: `client-${group.key}`,
      dispatchAmount: group.totalRemaining,
    };

    setSelectedContributions((prev) => [...prev, groupWithDispatchAmount]);
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Panel de Ventas</h2>

      <Modal show={!!approvalNotice} onHide={() => setApprovalNotice('')} centered>
        <Modal.Header closeButton>
          <Modal.Title>Autorización Solicitada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approvalNotice}
          <div className="text-muted small mt-2">Todavía no se ha cambiado nada. Verás el resultado en el menú de tu usuario → Autorizaciones.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setApprovalNotice('')}>Entendido</Button>
        </Modal.Footer>
      </Modal>

      <div className="row mb-4" style={{ textAlign: 'center' }}>
        <div className="col-md-6">
          <div className="card shadow h-100" style={{ minHeight: '170px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h5 className="card-title">En Planta</h5>
              <p
                className="card-text mb-0"
                style={{
                  fontSize: '2.75rem',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  textAlign: 'center',
                  color: 'var(--mm-green)',
                }}
              >
                {received}
              </p>
              <Button
                variant="outline-success"
                size="sm"
                className="mt-2"
                style={{ alignSelf: 'center' }}
                onClick={() => openAdjustEdit('received')}
              >
                Ajustar
              </Button>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow h-100" style={{ minHeight: '170px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h5 className="card-title">Por Llenar</h5>
              <p
                className="card-text mb-0"
                style={{
                  fontSize: '2.75rem',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  textAlign: 'center',
                  color: 'var(--mm-yellow)',
                }}
              >
                {remaining}
              </p>
              <Button
                variant="outline-warning"
                size="sm"
                className="mt-2"
                style={{ alignSelf: 'center' }}
                onClick={() => openAdjustEdit('remaining')}
              >
                Ajustar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal show={!!adjustField} onHide={() => setAdjustField(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar {ADJUST_FIELD_LABEL[adjustField]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adjustError && <div className="alert alert-danger py-2">{adjustError}</div>}
          <label className="form-label">{ADJUST_FIELD_LABEL[adjustField]}</label>
          <input
            type="number"
            className="form-control"
            value={adjustValue}
            onChange={(e) => setAdjustValue(Number(e.target.value))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAdjustField(null)} disabled={adjustSaving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleReviewAdjust} disabled={adjustSaving}>
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={adjustConfirmOpen} onHide={() => setAdjustConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Ajuste</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Ajustar <strong>{ADJUST_FIELD_LABEL[adjustField]}</strong> de{' '}
          <strong>{adjustCurrentValue}</strong> a <strong>{adjustValue}</strong>? Esto
          registra un ajuste administrativo, no un movimiento real.
          <div className="form-group mt-3 mb-0">
            <label className="form-label">Razón del ajuste (obligatoria)</label>
            <textarea className="form-control" rows={2} maxLength={300} value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAdjustConfirmOpen(false)} disabled={adjustSaving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmAdjust} disabled={adjustSaving || !adjustReason.trim()}>
            {adjustSaving ? 'Guardando...' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAdjustmentsModal} onHide={() => setShowAdjustmentsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Ajustes Administrativos de Hoy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {todaysAdjustments.length === 0 ? (
            <p className="text-muted mb-0">Sin ajustes registrados hoy.</p>
          ) : (
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>Ajuste</th>
                  <th>Hora</th>
                </tr>
              </thead>
              <tbody>
                {todaysAdjustments.map((entry) => (
                  <tr key={entry._id}>
                    <td>{entry.amount > 0 ? '+' : ''}{entry.amount} (En Planta)</td>
                    <td>{formatDateTime(entry.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdjustmentsModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card p-4 h-100">
            <h4>Agregar Cilindros Recibidos</h4>

            <div className="form-group position-relative">
              <label>Nombre del Cliente</label>
              <input
                type="text"
                className="form-control"
                value={clientName}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowDropdown(!!clientName)}
                placeholder="Ingrese el nombre del cliente"
              />

              {showDropdown && (
                <ul
                  className="dropdown-menu show"
                  style={{
                    width: '100%',
                    maxHeight: '260px',
                    overflowY: 'auto',
                    position: 'absolute',
                  }}
                >
                  {mergeSelectMode && (
                    <li
                      className="dropdown-item-text px-3 py-2"
                      style={{ borderBottom: '1px solid var(--mm-line)' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontSize: '0.85rem' }}>
                          Fusionar en <strong>{mergeTarget}</strong>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={cancelMergeSelectMode}
                        >
                          Cancelar
                        </button>
                      </div>
                    </li>
                  )}

                  {(mergeSelectMode
                    ? [...clientsList, ...orphanedClientNames].sort((a, b) => a.localeCompare(b))
                    : filteredClients
                  ).map(
                    (client) => {
                      const isMergeTarget =
                        mergeSelectMode &&
                        normalizeClientName(client) === normalizeClientName(mergeTarget);

                      return (
                        <li
                          key={client}
                          className="dropdown-item d-flex justify-content-between align-items-center"
                        >
                          {mergeSelectMode && !isMergeTarget && (
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={mergeSelectedSources.includes(client)}
                              onChange={() => toggleMergeSource(client)}
                            />
                          )}
                          <span
                            onClick={() => {
                              if (mergeSelectMode) {
                                if (!isMergeTarget) toggleMergeSource(client);
                              } else {
                                selectClient(client);
                              }
                            }}
                            style={{
                              cursor: isMergeTarget ? 'default' : 'pointer',
                              flexGrow: 1,
                              opacity: isMergeTarget ? 0.7 : 1,
                            }}
                          >
                            {client}
                          </span>
                          {mergeSelectMode && !isMergeTarget && <DeletedClientBadge name={client} />}
                          {isMergeTarget && (
                            <span
                              className="badge ml-1"
                              style={{
                                backgroundColor: 'var(--mm-panel-2)',
                                color: 'var(--mm-cyan)',
                                border: '1px solid rgba(103,232,249,0.4)',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                              }}
                            >
                              Destino
                            </span>
                          )}
                          {!mergeSelectMode && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 ml-2"
                                title="Editar nombre del cliente"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditClientModal(client);
                                }}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 ml-2"
                                title="Fusionar con otro cliente"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestMergeClient(client);
                                }}
                              >
                                <i className="bi bi-union"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 ml-2 text-danger"
                                title="Eliminar cliente"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestDeleteClient(client);
                                }}
                              >
                                <i className="bi bi-trash3"></i>
                              </button>
                            </>
                          )}
                        </li>
                      );
                    }
                  )}

                  {mergeSelectMode && (
                    <li
                      className="dropdown-item-text px-3 py-2"
                      style={{ borderTop: '1px solid var(--mm-line)' }}
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-warning w-100"
                        disabled={mergeSelectedSources.length === 0}
                        onClick={openMergeConfirm}
                      >
                        Fusionar {mergeSelectedSources.length}{' '}
                        {mergeSelectedSources.length === 1 ? 'cliente seleccionado' : 'clientes seleccionados'}
                      </button>
                    </li>
                  )}

                  {!mergeSelectMode && isNewClient && (
                    <li
                      className="dropdown-item text-primary"
                      onClick={() => openAddClientModal(clientName.trim())}
                    >
                      Agregar "{clientName.trim()}" como nuevo cliente
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="form-group mt-3">
              <label>Cilindros a Agregar</label>
              <input
                type="number"
                className="form-control"
                value={cylindersToAdd}
                onChange={(e) =>
                  setCylindersToAdd(
                    Math.max(0, parseInt(e.target.value) || 0)
                  )
                }
                placeholder="Número de cilindros"
              />
            </div>

            <div className="form-group mt-3">
              <label>Gas</label>
              <select
                className="form-control"
                value={contributionGas}
                onChange={(e) => setContributionGas(e.target.value)}
              >
                <option value="">Elige el gas…</option>
                {GASES.map((g) => (
                  <option key={g} value={g}>{GAS_LABEL[g]}</option>
                ))}
              </select>
            </div>

            {isCgcClient(clientName) && (
              <div className="form-group mt-3">
                <label>Traído por (cliente que devolvió los cilindros de CGC)</label>
                <select
                  className="form-control"
                  value={contributionBroughtBy}
                  onChange={(e) => setContributionBroughtBy(e.target.value)}
                >
                  <option value="">Elige el cliente…</option>
                  <option value={CGC_CLIENT}>Ninguno — estaban en la empresa</option>
                  {clientNames.filter((name) => !isCgcClient(name)).map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="btn btn-primary mt-3"
              onClick={handleAddCylinders}
            >
              Agregar Cilindros
            </button>

            <div className="d-flex align-items-start mt-3" style={{ gap: '8px' }}>
              <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.9rem', marginTop: '2px' }}></i>
              <small className="text-muted">
                Agregar una contribución aumenta tanto "En Planta" como "Por Llenar".
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <div className="card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 className="mb-0">Despachar Cilindros</h4>

              <div
                className="d-flex align-items-center"
                title="Cilindros despachados hoy"
                style={{
                  backgroundColor: 'var(--mm-panel-2)',
                  border: '1px solid var(--mm-line)',
                  borderRadius: '999px',
                  padding: '6px 14px',
                  gap: '8px',
                }}
              >
                <i className="bi bi-truck" style={{ color: 'var(--mm-cyan)' }}></i>
                <span
                  style={{
                    fontSize: '0.7rem',
                    color: 'var(--mm-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  Hoy
                </span>
                <span
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: 'var(--mm-cyan)',
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {dispatchedToday}
                </span>
              </div>
            </div>

            <div className="form-group">
              <div className="mb-3">
                <h6>Contribuciones Seleccionadas:</h6>

                {selectedContributions.length > 0 ? (
                  <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                    {selectedContributions.map((entry, idx) => (
                      <div
                        key={`${entry.client}-${idx}`}
                        className="d-flex align-items-center"
                        style={{
                          backgroundColor: '#12B8B2',
                          color: '#06222B',
                          borderRadius: '20px',
                          padding: '6px 12px',
                          fontSize: '0.9rem',
                        }}
                      >
                        {entry.client}
                        <DeletedClientBadge name={entry.client} />
                        <span style={{ marginLeft: 4, fontSize: '0.8rem' }}>({gasLabel(entry.gas)})</span> —

                        <input
                          type="number"
                          value={entry.dispatchAmount}
                          min={1}
                          max={entry.totalRemaining}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);

                            if (
                              !isNaN(val) &&
                              val >= 0 &&
                              val <= entry.totalRemaining
                            ) {
                              const updated = [...selectedContributions];
                              updated[idx].dispatchAmount = val;
                              setSelectedContributions(updated);
                            }
                          }}
                          style={{
                            width: '70px',
                            marginLeft: '10px',
                            marginRight: '6px',
                            border: 'none',
                            borderRadius: '4px',
                            textAlign: 'center',
                          }}
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setSelectedContributions((prev) =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#06222B',
                            fontWeight: 'bold',
                            marginLeft: '5px',
                            cursor: 'pointer',
                          }}
                          aria-label="Eliminar"
                          title="Quitar"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <div className="mt-2 w-100">
                      Total a Despachar:{' '}
                      <strong>
                        {selectedContributions.reduce(
                          (sum, e) => sum + (e.dispatchAmount || 0),
                          0
                        )}
                      </strong>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">
                    No hay contribuciones seleccionadas.
                  </p>
                )}
              </div>
            </div>

            <div className="form-group mt-3">
              <label>Número de Documento</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  value={dispatchNote}
                  onChange={(e) => setDispatchNote(e.target.value)}
                  onBlur={() => setTimeout(() => setDispatchDocumentSuggestions([]), 150)}
                  placeholder="Ej: 00123"
                  autoComplete="off"
                />
                {dispatchDocumentSuggestions.length > 0 && (
                  <div className="mm-suggestions">
                    {dispatchDocumentSuggestions.map((doc) => (
                      <button
                        type="button"
                        key={`${doc.type}-${doc.number}`}
                        className="mm-suggestion-item"
                        onClick={() => pickDispatchDocumentSuggestion(doc)}
                      >
                        <strong>{doc.type === 'conduce' ? 'Conduce' : 'Factura'} {doc.number}</strong>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {doc.client} · {formatDay(doc.date)} · RD$
                          {Number(doc.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <small className="text-muted">Últimas facturas y conduces de Ventas al escribir el número o el nombre del cliente.</small>
            </div>

            <div className="form-group mt-3">
              <label>
                {isCgcClient(selectedContributions[0]?.client)
                  ? 'Cliente Destino (obligatorio: ¿quién recibe los cilindros de CGC?)'
                  : 'Cliente Destino (si es un intercambio)'}
              </label>
              <select
                className="form-control"
                value={dispatchDestinationClient}
                onChange={(e) => setDispatchDestinationClient(e.target.value)}
              >
                <option value="">
                  {isCgcClient(selectedContributions[0]?.client) ? 'Elige el cliente…' : 'No es un intercambio'}
                </option>
                {isCgcClient(selectedContributions[0]?.client) && (
                  <option value={CGC_CLIENT}>Uso interno — se quedan en la empresa</option>
                )}
                {clientNames
                  .filter((name) => !selectedContributions.some((e) => e.client === name))
                  .map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
              </select>
              {dispatchDestinationClient && (
                <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                  Se descuenta del inventario de{' '}
                  <strong>{selectedContributions[0]?.client || '—'}</strong>, pero
                  se registrará como destinado a{' '}
                  <strong>{dispatchDestinationClient}</strong>.
                </div>
              )}
            </div>

            <button
              className="btn btn-danger mt-3"
              onClick={handleReviewDispatch}
              disabled={
                selectedContributions.length === 0 ||
                !dispatchNote ||
                selectedContributions.every((e) => e.dispatchAmount <= 0)
              }
            >
              Despachar Cilindros
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={showDispatchSuccessModal}
        onHide={() => setShowDispatchSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Despacho registrado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>{dispatchSuccessMessage}</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => setShowDispatchSuccessModal(false)}
          >
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Contribución</Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          ¿Desea agregar <strong>{pendingContribution?.quantity}</strong>{' '}
          cilindros de <strong>{gasLabel(pendingContribution?.gas)}</strong> para{' '}
          <strong>{pendingContribution?.client}</strong> como tipo{' '}
          <strong>{pendingContribution?.type}</strong>
          {pendingContribution?.broughtBy && (
            <>, traídos por <strong>{pendingContribution.broughtBy}</strong></>
          )}?
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmAddCylinders}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={pelliqueWarningOpen}
        onHide={() => (pelliqueActionSaving ? null : setPelliqueWarningOpen(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Tanques Prestados Pendientes</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {pelliqueActionError && <div className="alert alert-danger">{pelliqueActionError}</div>}
          <strong>{selectedContributions[0]?.client}</strong> tiene{' '}
          <strong>{pelliqueOutstandingByClient[selectedContributions[0]?.client] || 0} tanques</strong>{' '}
          prestados (pellique) sin devolver. ¿Cómo deseas continuar?
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleAdjustToAvailableBalance} disabled={pelliqueActionSaving}>
            Ajustar a Balance Disponible
          </Button>
          <Button variant="danger" onClick={handleReturnPelliqueAndDispatch} disabled={pelliqueActionSaving}>
            {pelliqueActionSaving ? 'Procesando...' : 'Devolver Pellique y Despachar Todo'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!receivingDev} onHide={() => setReceivingDev(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Recibir Devolución — Pedido {receivingDev?.request.pedidoNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {receiveDevError && <div className="alert alert-danger">{receiveDevError}</div>}
          <p className="small text-muted">
            El cliente pidió devolver {receivingDev?.request.quantity}. ¿Cuántos llegaron? Vuelven a "En Planta" sin afectar "Por Llenar".
          </p>
          <input
            type="number"
            className="form-control"
            min={1}
            max={receivingDev?.request.returnable || 0}
            value={receivingDev?.quantity ?? 0}
            onChange={(e) => setReceivingDev((s) => ({ ...s, quantity: e.target.value }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReceivingDev(null)}>Cancelar</Button>
          <Button variant="warning" onClick={handleReceiveDevolucion}>Registrar en Planta</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deliveringDispatch} onHide={() => setDeliveringDispatch(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Entrega — {deliveringDispatch?.entry.noteNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deliverError && <div className="alert alert-danger">{deliverError}</div>}
          <p className="small text-muted">
            Salieron {deliveringDispatch ? deliveringDispatch.entry.cantidad - (deliveringDispatch.entry.totalReturned || 0) : 0} cilindros.
            ¿Cuántos recibió el cliente? Si recibió menos, los que faltan vuelven a "En Planta" automáticamente (sin afectar "Por Llenar").
          </p>
          <input
            type="number"
            className="form-control"
            min={0}
            max={deliveringDispatch ? deliveringDispatch.entry.cantidad - (deliveringDispatch.entry.totalReturned || 0) : 0}
            value={deliveringDispatch?.received ?? 0}
            onChange={(e) => setDeliveringDispatch((d) => ({ ...d, received: e.target.value }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeliveringDispatch(null)}>Cancelar</Button>
          <Button variant="success" onClick={handleConfirmDelivery}>Confirmar Entrega</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Despacho</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          ¿Estás seguro de que deseas despachar{' '}
          <strong>
            {selectedContributions.reduce(
              (sum, e) => sum + (e.dispatchAmount || 0),
              0
            )}
          </strong>{' '}
          cilindros con la nota <strong>{dispatchNote}</strong>?
          {dispatchDestinationClient && (
            <div className="mt-2">
              Intercambio: se descuenta de <strong>{selectedContributions[0]?.client || '—'}</strong>, destinado a{' '}
              <strong>{dispatchDestinationClient}</strong>.
            </div>
          )}
          <div className="mt-3">
            {dispatchPreview && (
              <div className="small text-muted mb-2">
                Pedido <strong>{dispatchPreview.number}</strong> de CG Connect: el cliente pidió{' '}
                {{ we_deliver: 'que se lo llevemos', client_picks: 'retirarlo en planta' }[dispatchPreview.legs?.filled] || 'coordinar la entrega'}
                {dispatchPreview.requested > 0 && ` · ${dispatchPreview.sent} de ${dispatchPreview.requested} ya despachados`}.
              </div>
            )}
            <div className="btn-group w-100">
              <button type="button" className={`btn btn-sm ${dispatchMethod === 'entrega' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setDispatchMethod('entrega')}>
                Entrega
              </button>
              <button type="button" className={`btn btn-sm ${dispatchMethod === 'retiro' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setDispatchMethod('retiro')}>
                Retiro en planta
              </button>
            </div>
            <div className="small text-muted mt-1">
              {dispatchMethod === 'retiro'
                ? 'El cliente se los llevó: queda entregado ahora mismo.'
                : 'Se confirma la entrega después, con ✓ en la tabla de despachos.'}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancelar
          </Button>

          <Button
            variant="danger"
            onClick={() => {
              setShowConfirmation(false);
              handleDispatch();
            }}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchModal}
        onHide={() => setShowDispatchModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Despacho</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          ¿Está seguro que desea despachar {cylindersToDispatch} cilindros con
          el número de documento "{dispatchNote}"?
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDispatchModal(false)}>
            Cancelar
          </Button>

          <Button variant="danger" onClick={handleDispatch}>
            Confirmar Despacho
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Error de Despacho</Modal.Title>
        </Modal.Header>

        <Modal.Body>{dispatchError}</Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddClientModal}
        onHide={() => (addClientSaving ? null : setShowAddClientModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Agregar Nuevo Cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {addClientError && (
            <div className="alert alert-danger">{addClientError}</div>
          )}

          <div className="form-group">
            <label>Nombre del Cliente</label>
            <input
              type="text"
              className="form-control"
              value={newClientNameInput}
              onChange={(e) => {
                setNewClientNameInput(e.target.value);
                setAddClientError('');
                setAddClientSimilarMatches([]);
              }}
              autoFocus
            />
          </div>

          {addClientSimilarMatches.length > 0 && (
            <div className="alert alert-warning">
              Este nombre es muy similar a{' '}
              {addClientSimilarMatches.length === 1 ? 'un cliente existente' : 'clientes existentes'}:
              <ul className="mb-1 mt-1">
                {addClientSimilarMatches.map((m) => (
                  <li key={m.name}>{m.name}</li>
                ))}
              </ul>
              Si es un cliente distinto, presione "Agregar de Todos Modos" para
              confirmar.
            </div>
          )}

          <p className="text-muted mt-2 mb-0">
            Confirme que desea crear el cliente{' '}
            <strong>"{newClientNameInput.trim()}"</strong>. Esta acción lo
            agregará a la lista de clientes disponible para futuras
            contribuciones y despachos.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddClientModal(false)}
            disabled={addClientSaving}
          >
            Cancelar
          </Button>
          <Button
            variant={addClientSimilarMatches.length > 0 ? 'warning' : 'primary'}
            onClick={
              addClientSimilarMatches.length > 0
                ? proceedAddClientAnyway
                : confirmAddClient
            }
            disabled={addClientSaving || !newClientNameInput.trim()}
          >
            {addClientSaving
              ? 'Guardando...'
              : addClientSimilarMatches.length > 0
              ? 'Agregar de Todos Modos'
              : 'Confirmar y Agregar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditClientModal}
        onHide={() => (editClientSaving ? null : setShowEditClientModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Nombre de Cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editClientError && (
            <div className="alert alert-danger">{editClientError}</div>
          )}

          <div className="form-group">
            <label>Nombre Actual</label>
            <input
              type="text"
              className="form-control"
              value={editClientOldName}
              disabled
            />
          </div>

          <div className="form-group">
            <label>Nuevo Nombre</label>
            <input
              type="text"
              className="form-control"
              value={editClientNewName}
              onChange={(e) => {
                setEditClientNewName(e.target.value);
                setEditClientError('');
                setEditClientSimilarMatches([]);
              }}
              autoFocus
            />
          </div>

          {editClientSimilarMatches.length > 0 && (
            <div className="alert alert-warning">
              Este nombre es muy similar a{' '}
              {editClientSimilarMatches.length === 1 ? 'un cliente existente' : 'clientes existentes'}:
              <ul className="mb-1 mt-1">
                {editClientSimilarMatches.map((m) => (
                  <li key={m.name}>{m.name}</li>
                ))}
              </ul>
              Si es un cliente distinto, presione "Guardar de Todos Modos" para
              confirmar.
            </div>
          )}

          <p className="text-muted mt-2 mb-0">
            Esto actualizará el nombre del cliente en sus contribuciones y
            despachos existentes.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditClientModal(false)}
            disabled={editClientSaving}
          >
            Cancelar
          </Button>
          <Button
            variant={editClientSimilarMatches.length > 0 ? 'warning' : 'primary'}
            onClick={
              editClientSimilarMatches.length > 0
                ? proceedEditClientAnyway
                : confirmEditClientName
            }
            disabled={editClientSaving || !editClientNewName.trim()}
          >
            {editClientSaving
              ? 'Guardando...'
              : editClientSimilarMatches.length > 0
              ? 'Guardar de Todos Modos'
              : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteClientModal}
        onHide={() => (deleteClientSaving ? null : setShowDeleteClientModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar Cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteClientError && (
            <div className="alert alert-danger">{deleteClientError}</div>
          )}

          <p>
            ¿Está seguro que desea eliminar al cliente{' '}
            <strong>{deleteClientTarget}</strong>? Dejará de aparecer para
            nuevas contribuciones o despachos.
            {groupedContributions.some((g) => g.client === deleteClientTarget) && (
              <>
                {' '}
                Este cliente tiene cilindros pendientes sin despachar — esas
                contribuciones seguirán existiendo y podrán gestionarse
                normalmente, pero se mostrarán marcadas como{' '}
                <strong>"Cliente eliminado"</strong>.
              </>
            )}
          </p>

          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Esta acción requiere credenciales de administrador y quedará
            registrada en el registro de actividades.
          </p>

          <div className="form-group">
            <label>Usuario Administrador</label>
            <input
              type="text"
              className="form-control"
              value={deleteClientAdminUser}
              onChange={(e) => {
                setDeleteClientAdminUser(e.target.value);
                setDeleteClientError('');
              }}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Contraseña de Administrador</label>
            <input
              type="password"
              className="form-control"
              value={deleteClientAdminPass}
              onChange={(e) => {
                setDeleteClientAdminPass(e.target.value);
                setDeleteClientError('');
              }}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteClientModal(false)}
            disabled={deleteClientSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteClient}
            disabled={
              deleteClientSaving ||
              !deleteClientAdminUser.trim() ||
              !deleteClientAdminPass
            }
          >
            {deleteClientSaving ? 'Verificando...' : 'Eliminar Cliente'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteClientSuccessModal}
        onHide={() => setShowDeleteClientSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cliente Eliminado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>{deleteClientSuccessMessage}</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => setShowDeleteClientSuccessModal(false)}
          >
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMergeClientModal}
        onHide={() => {
          if (mergeClientSaving) return;
          setShowMergeClientModal(false);
          setShowDropdown(true);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Fusión de Clientes</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {mergeClientError && (
            <div className="alert alert-danger">{mergeClientError}</div>
          )}

          <p>
            Está a punto de fusionar {mergeSelectedSources.length === 1 ? 'el siguiente cliente' : 'los siguientes clientes'} en{' '}
            <strong>{mergeTarget}</strong>:
          </p>

          <ul>
            {mergeSelectedSources.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>

          <p>
            Todas sus contribuciones, despachos, órdenes de llenado y
            pelliques pasarán a aparecer bajo <strong>{mergeTarget}</strong>, y{' '}
            {mergeSelectedSources.length === 1 ? 'ese cliente dejará' : 'esos clientes dejarán'} de existir
            como {mergeSelectedSources.length === 1 ? 'cliente separado' : 'clientes separados'}.
          </p>

          <p style={{ color: 'var(--mm-red)', fontWeight: 500 }}>
            Esta acción no se puede deshacer.
          </p>

          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Quedará registrada en el registro de actividades.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowMergeClientModal(false);
              setShowDropdown(true);
            }}
            disabled={mergeClientSaving}
          >
            Cancelar
          </Button>
          <Button variant="warning" onClick={confirmMergeClient} disabled={mergeClientSaving}>
            {mergeClientSaving
              ? 'Fusionando...'
              : `Fusionar ${mergeSelectedSources.length} ${
                  mergeSelectedSources.length === 1 ? 'cliente' : 'clientes'
                }`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMergeClientSuccessModal}
        onHide={() => setShowMergeClientSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Clientes Fusionados</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>{mergeClientSuccessMessage}</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => setShowMergeClientSuccessModal(false)}
          >
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchEditModal}
        onHide={() => (dispatchEditSaving ? null : setShowDispatchEditModal(false))}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Despacho</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchEditError && (
            <div className="alert alert-danger">{dispatchEditError}</div>
          )}
          {approvalReasonField}

          <div className="row">
            <div className="col-md-6 form-group">
              <label>Número de Documento</label>
              <input
                type="text"
                className="form-control"
                value={dispatchEditForm.noteNumber}
                onChange={(e) =>
                  setDispatchEditForm((f) => ({ ...f, noteNumber: e.target.value }))
                }
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Despachado Por</label>
              <input
                type="text"
                className="form-control"
                value={dispatchEditForm.dispatchedBy}
                onChange={(e) =>
                  setDispatchEditForm((f) => ({ ...f, dispatchedBy: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label>Cliente Destino (si es un intercambio)</label>
            <select
              className="form-control"
              value={dispatchEditForm.destinationClient}
              onChange={(e) =>
                setDispatchEditForm((f) => ({ ...f, destinationClient: e.target.value }))
              }
            >
              <option value="">No es un intercambio</option>
              {clientNames
                .filter((name) => !dispatchEditForm.contributions.some((c) => c.client === name))
                .map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
            </select>
          </div>

          <label className="mt-2">Cilindros por Cliente</label>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {dispatchEditForm.contributions.map((c, index) => (
                <tr key={`${c.entryId}-${index}`}>
                  <td>
                    {c.client}
                    <DeletedClientBadge name={c.client} />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={c.amount}
                      onChange={(e) =>
                        updateDispatchEditAmount(index, e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchEditModal(false)}
            disabled={dispatchEditSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={saveDispatchEdit}
            disabled={dispatchEditSaving}
          >
            {dispatchEditSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchDeleteModal}
        onHide={() => (dispatchDeleteSaving ? null : setShowDispatchDeleteModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchDeleteError && (
            <div className="alert alert-danger">{dispatchDeleteError}</div>
          )}
          {approvalReasonField}
          ¿Está seguro que desea eliminar el despacho con documento{' '}
          <strong>{dispatchDeleteTarget?.noteNumber}</strong> por{' '}
          <strong>{dispatchDeleteTarget?.cantidad}</strong> cilindros? Los
          cilindros despachados volverán a estar disponibles. Esta acción no
          se puede deshacer.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchDeleteModal(false)}
            disabled={dispatchDeleteSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteDispatch}
            disabled={dispatchDeleteSaving}
          >
            {dispatchDeleteSaving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDispatchReturnModal}
        onHide={() => (dispatchReturnSaving ? null : setShowDispatchReturnModal(false))}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Registrar Devolución — {returningDispatch?.noteNumber}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dispatchReturnError && (
            <div className="alert alert-danger">{dispatchReturnError}</div>
          )}

          <p className="text-muted small">
            Indique cuántos cilindros de cada línea no fueron aceptados por el
            cliente. Se sumarán de vuelta a "En Planta" y a lo pendiente de
            despachar en la contribución de origen — no afectan "Por Llenar",
            ya que estos tanques ya estaban llenos.
          </p>

          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Despachado</th>
                <th>Ya Devuelto</th>
                <th>Cantidad a Devolver</th>
              </tr>
            </thead>
            <tbody>
              {dispatchReturnLines.map((line, index) => (
                <tr key={`${line.entryId}-${index}`}>
                  <td>
                    {line.client}
                    <DeletedClientBadge name={line.client} />
                  </td>
                  <td>{line.originalAmount}</td>
                  <td>{line.alreadyReturned}</td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max={line.remaining}
                      className="form-control"
                      placeholder="0"
                      disabled={line.remaining <= 0}
                      value={line.returnQty}
                      onChange={(e) => updateDispatchReturnQty(index, e.target.value)}
                    />
                    {line.remaining <= 0 && (
                      <small className="text-muted">Nada pendiente</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="form-group">
            <label>Motivo (opcional)</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Ej: Cliente rechazó 10 tanques por daño en el sello."
              value={dispatchReturnReason}
              onChange={(e) => setDispatchReturnReason(e.target.value)}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDispatchReturnModal(false)}
            disabled={dispatchReturnSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="warning"
            onClick={saveDispatchReturn}
            disabled={dispatchReturnSaving}
          >
            {dispatchReturnSaving ? 'Registrando...' : 'Registrar Devolución'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showManageModal}
        onHide={() => setShowManageModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Contribuciones de {manageGroup?.client}
            <DeletedClientBadge name={manageGroup?.client} />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {manageGroup && manageGroup.entries.length > 0 ? (
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th>Restante</th>
                  <th>Fecha</th>
                  <th>Usuario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {manageGroup.entries.map((entry) => {
                  const hasDispatches =
                    (entry.remainingForDispatch || 0) < (entry.amount || 0);

                  return (
                    <tr key={entry._id}>
                      <td>{entry.type || '–'}</td>
                      <td>{entry.amount}</td>
                      <td>{entry.remainingForDispatch}</td>
                      <td>
                        {entry.date
                          ? formatDateShort(entry.date)
                          : '–'}
                      </td>
                      <td>{entry.addedBy || '–'}</td>
                      <td>
                        <div className="d-flex" style={{ gap: '4px' }}>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            title="Editar"
                            onClick={() => openEditModal(entry)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title={
                              hasDispatches
                                ? 'No se puede eliminar: ya tiene despachos asociados'
                                : 'Eliminar'
                            }
                            disabled={hasDispatches}
                            onClick={() => requestDeleteEntry(entry)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-muted mb-0">
              No hay contribuciones pendientes para este cliente.
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManageModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => (editSaving ? null : setShowEditModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Contribución</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editError && <div className="alert alert-danger">{editError}</div>}
          {approvalReasonField}

          <div className="form-group">
            <label>
              Cliente
              <DeletedClientBadge
                name={editingEntry?.client || manageGroup?.client}
              />
            </label>
            <input
              type="text"
              className="form-control"
              value={editingEntry?.client || manageGroup?.client || ''}
              disabled
            />
          </div>

          <div className="form-group">
            <label>Tipo</label>
            <select
              className="form-control"
              value={editForm.type}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, type: e.target.value }))
              }
            >
              <option value="CC">CC</option>
              <option value="PC">PC</option>
            </select>
          </div>

          <div className="form-group">
            <label>Gas</label>
            <select
              className="form-control"
              value={editForm.gas}
              onChange={(e) => setEditForm((f) => ({ ...f, gas: e.target.value }))}
            >
              <option value="" disabled>Sin dato</option>
              {GASES.map((g) => (
                <option key={g} value={g}>{GAS_LABEL[g]}</option>
              ))}
            </select>
          </div>

          {isCgcClient(editingEntry?.client) && (
            <div className="form-group">
              <label>Traído por</label>
              <select
                className="form-control"
                value={editForm.broughtBy}
                onChange={(e) => setEditForm((f) => ({ ...f, broughtBy: e.target.value }))}
              >
                <option value="" disabled>Sin dato</option>
                <option value={CGC_CLIENT}>Ninguno — estaban en la empresa</option>
                {clientNames.filter((name) => !isCgcClient(name)).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Cantidad</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={editForm.quantity}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, quantity: e.target.value }))
              }
            />
          </div>

          <div className="form-group">
            <label>Fecha</label>
            <input
              type="date"
              className="form-control"
              value={editForm.date}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, date: e.target.value }))
              }
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            disabled={editSaving}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={editSaving}>
            {editSaving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirmModal}
        onHide={() => (deleteSaving ? null : setShowDeleteConfirmModal(false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteError && <div className="alert alert-danger">{deleteError}</div>}
          {approvalReasonField}
          ¿Está seguro que desea eliminar la contribución de{' '}
          <strong>{deleteTarget?.amount}</strong> cilindros (
          <strong>{deleteTarget?.type}</strong>) para{' '}
          <strong>{deleteTarget?.client}</strong>? Esta acción no se puede
          deshacer.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirmModal(false)}
            disabled={deleteSaving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteEntry}
            disabled={deleteSaving}
          >
            {deleteSaving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="row">
        <div className="col-md-12 mb-4">
          <div className="card p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-end" style={{ gap: '12px' }}>
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <h4 className="mb-0">Contribuciones de Cilindros</h4>
                {hasAdjustmentToday && (
                  <span
                    className="badge badge-info"
                    style={{ cursor: 'pointer' }}
                    title="Ver ajustes administrativos de hoy"
                    onClick={() => setShowAdjustmentsModal(true)}
                  >
                    Ajuste hoy
                  </span>
                )}
              </div>

              <div>
                <label className="form-label mb-1">Buscar cliente</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Caribbean Gas Company"
                  value={contributionSearch}
                  onChange={(e) => setContributionSearch(e.target.value)}
                />
              </div>
            </div>

            {searchedContributions.length > 0 ? (
              <>
                <table className="table table-striped table-bordered mt-3">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Total Original</th>
                      <th>Restantes</th>
                      <th>Prestados</th>
                      <th>Entradas Pendientes</th>
                      <th>Última Entrada</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedContributions.map((group) => {
                      const isSelected = selectedContributions.some(
                        (entry) => entry.key === group.key
                      );

                      return (
                        <tr
                          key={group.key}
                          className={isSelected ? 'table-warning' : ''}
                        >
                          <td>
                            {group.client}
                            <DeletedClientBadge name={group.client} />
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{gasLabel(group.gas)}</div>
                          </td>
                          <td>{group.totalOriginal}</td>
                          <td>{group.totalRemaining}</td>
                          <td>{pelliqueOutstandingByClient[group.client] || '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              title="Ver contribuciones pendientes de este cliente"
                              onClick={() => openManageModal(group)}
                            >
                              {group.entries.length}
                            </button>
                          </td>
                          <td>
                            {group.latestDate
                              ? formatDateTime(group.latestDate)
                              : '–'}
                          </td>
                          <td>
                            <div className="d-flex" style={{ gap: '4px' }}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Agregar al despacho"
                                onClick={() => selectForDispatch(group)}
                                disabled={isSelected}
                              >
                                <i className="bi bi-truck"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Editar o eliminar contribuciones"
                                onClick={() => openManageModal(group)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Página {contributionPage} de {totalContributionPages} —{' '}
                    {searchedContributions.length} clientes con cilindros
                    pendientes
                  </small>

                  <div className="btn-group">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={contributionPage === 1}
                      onClick={() =>
                        setContributionPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      Anterior
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={contributionPage === totalContributionPages}
                      onClick={() =>
                        setContributionPage((prev) =>
                          Math.min(totalContributionPages, prev + 1)
                        )
                      }
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted mt-3">
                {contributionSearch.trim()
                  ? 'No se encontraron clientes que coincidan con la búsqueda.'
                  : 'No hay contribuciones pendientes.'}
              </p>
            )}
          </div>
        </div>

        {(dispatches.some((d) => d.method === 'entrega' && !d.delivered) || pendingDevoluciones.length > 0) && (
          <div className="col-md-12 mb-3">
            <div className="card p-4">
              {dispatches.some((d) => d.method === 'entrega' && !d.delivered) && (
                <>
                  <h5>Por Entregar</h5>
                  <table className="table table-sm mb-3">
                    <tbody>
                      {dispatches.filter((d) => d.method === 'entrega' && !d.delivered).map((d) => (
                        <tr key={d._id}>
                          <td>{d.noteNumber}{d.ventas?.pedidoNumber && <span className="text-muted small"> · Pedido {d.ventas.pedidoNumber}</span>}</td>
                          <td>{d.cantidad - (d.totalReturned || 0)} cilindros</td>
                          <td>{[...new Set(d.contributions.map((c) => c.destinationClient || c.client))].join(', ')}</td>
                          <td className="small text-muted">{d.timestamp}</td>
                          <td className="text-right">
                            <button className="btn btn-sm btn-outline-success" onClick={() => { setDeliverError(''); setDeliveringDispatch({ entry: d, received: d.cantidad - (d.totalReturned || 0) }); }}>
                              <i className="bi bi-check2-circle"></i> Entregado
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {pendingDevoluciones.length > 0 && (
                <>
                  <h5>Devoluciones Pendientes</h5>
                  <p className="small text-muted mb-2">Pedidas por el cliente (CG Connect) o por ventas. El inventario cambia cuando confirmes que llegaron.</p>
                  <table className="table table-sm mb-0">
                    <tbody>
                      {pendingDevoluciones.map((r) => (
                        <tr key={r._id}>
                          <td>{r.quantity} cilindros · {r.client}</td>
                          <td className="small">Pedido {r.pedidoNumber}{r.notes?.length ? ` · ${r.notes.join(', ')}` : ''}</td>
                          <td className="small text-muted">{r.reason || '—'}</td>
                          <td className="text-right">
                            <button className="btn btn-sm btn-outline-warning" disabled={r.returnable <= 0}
                              onClick={() => { setReceiveDevError(''); setReceivingDev({ request: r, quantity: Math.min(r.quantity, r.returnable) }); }}>
                              <i className="bi bi-arrow-return-left"></i> Llegaron
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        <div className="col-md-12">
          <div className="card p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-end" style={{ gap: '12px' }}>
              <h4 className="mb-0">Despachos</h4>

              <div className="d-flex flex-wrap align-items-end" style={{ gap: '8px' }}>
                <div>
                  <label className="form-label mb-1">Filtro</label>
                  <select
                    className="form-select"
                    value={dispatchFilter}
                    onChange={(e) => setDispatchFilter(e.target.value)}
                  >
                    <option value="today">Hoy</option>
                    <option value="week">Esta semana</option>
                    <option value="month">Este mes</option>
                    <option value="custom">Mes personalizado</option>
                  </select>
                </div>

                {dispatchFilter === 'custom' && (
                  <div>
                    <label className="form-label mb-1">Mes</label>
                    <input
                      type="month"
                      className="form-control"
                      value={customDispatchMonth}
                      max={`${new Date().getFullYear()}-${String(
                        new Date().getMonth() + 1
                      ).padStart(2, '0')}`}
                      onChange={(e) => setCustomDispatchMonth(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="form-label mb-1">Buscar Núm. de Documento</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: F1157"
                    value={dispatchSearch}
                    onChange={(e) => setDispatchSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {searchedDispatches.length > 0 ? (
              <>
                <table className="table table-striped mt-3">
                  <thead>
                    <tr>
                      <th>Cantidad</th>
                      <th>Doc. No.</th>
                      <th>Despachado Por</th>
                      <th>Fecha y Hora</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedDispatches.map((entry) => {
                      const totalReturned = entry.totalReturned || 0;
                      const fullyReturned = totalReturned >= entry.cantidad;
                      return (
                        <tr key={entry._id}>
                          <td>
                            {entry.cantidad}
                            {totalReturned > 0 && (
                              <span
                                className="badge ml-1"
                                title={`${totalReturned} cilindros devueltos`}
                                style={{
                                  backgroundColor: 'var(--mm-panel-2)',
                                  color: 'var(--mm-yellow)',
                                  border: '1px solid rgba(230,190,40,0.4)',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                }}
                              >
                                ↩ {totalReturned} devueltos
                              </span>
                            )}
                          </td>
                          <td>
                            {entry.noteNumber}
                            {entry.method && (
                              <div className="small text-muted">
                                {entry.method === 'retiro'
                                  ? 'Retirado en planta'
                                  : entry.delivered
                                    ? `Entregado${entry.receivedQuantity != null && entry.receivedQuantity < entry.cantidad - totalReturned ? ` · recibió ${entry.receivedQuantity}` : ''}`
                                    : <span className="text-mm-yellow">Por entregar</span>}
                                {entry.ventas?.pedidoNumber && ` · Pedido ${entry.ventas.pedidoNumber}`}
                              </div>
                            )}
                          </td>
                          <td>{entry.dispatchedBy}</td>
                          <td>{entry.timestamp}</td>
                          <td>
                            <div className="d-flex" style={{ gap: '4px' }}>
                              {entry.method === 'entrega' && !entry.delivered && (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  title="Confirmar entrega"
                                  onClick={() => { setDeliverError(''); setDeliveringDispatch({ entry, received: entry.cantidad - totalReturned }); }}
                                >
                                  <i className="bi bi-check2-circle"></i>
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-outline-warning"
                                title={fullyReturned ? 'Este despacho ya fue devuelto por completo' : 'Registrar devolución'}
                                disabled={fullyReturned}
                                onClick={() => openDispatchReturnModal(entry)}
                              >
                                <i className="bi bi-arrow-return-left"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title={totalReturned > 0 ? 'No se puede editar: ya tiene una devolución registrada' : 'Editar despacho'}
                                disabled={totalReturned > 0}
                                onClick={() => openDispatchEditModal(entry)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Eliminar despacho"
                                onClick={() => requestDeleteDispatch(entry)}
                              >
                                <i className="bi bi-trash3"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Página {dispatchPage} de {totalDispatchPages} —{' '}
                    {searchedDispatches.length} despachos
                  </small>

                  <div className="btn-group">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={dispatchPage === 1}
                      onClick={() =>
                        setDispatchPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      Anterior
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={dispatchPage === totalDispatchPages}
                      onClick={() =>
                        setDispatchPage((prev) =>
                          Math.min(totalDispatchPages, prev + 1)
                        )
                      }
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted mt-3">
                {dispatchSearch.trim()
                  ? 'No se encontraron despachos que coincidan con la búsqueda.'
                  : 'No hay despachos para este período.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;